// Hex to RGB
export const hexToRgb = (hex: string): [number, number, number] | null => {
  const clean = hex.replace('#', '');
  if (clean.length !== 6) return null;
  const r = parseInt(clean.substring(0, 2), 16);
  const g = parseInt(clean.substring(2, 4), 16);
  const b = parseInt(clean.substring(4, 6), 16);
  if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
  return [r, g, b];
};

// RGB to HSL
export const rgbToHsl = (r: number, g: number, b: number): [number, number, number] => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  return [h * 360, s, l];
};

// 色の距離（HSL空間）
export const colorDistance = (hex1: string, hex2: string): number => {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  if (!rgb1 || !rgb2) return Infinity;
  const [h1, s1, l1] = rgbToHsl(...rgb1);
  const [h2, s2, l2] = rgbToHsl(...rgb2);
  const hDiff = Math.min(Math.abs(h1 - h2), 360 - Math.abs(h1 - h2)) / 180;
  const sDiff = Math.abs(s1 - s2);
  const lDiff = Math.abs(l1 - l2);
  return Math.sqrt(hDiff * hDiff * 4 + sDiff * sDiff + lDiff * lDiff);
};

// 色相環順（赤→橙→黄→緑→青→紫→ピンク→茶→無彩色）
export const COLOR_GROUPS: { name: string; hex: string }[] = [
  // Red (0°)
  { name: 'crimson', hex: '#dc143c' },
  { name: 'red', hex: '#e53e3e' },
  { name: 'coral', hex: '#ff7f50' },
  // Orange (30°)
  { name: 'orange', hex: '#dd6b20' },
  { name: 'peach', hex: '#ffb088' },
  // Yellow (60°)
  { name: 'gold', hex: '#daa520' },
  { name: 'yellow', hex: '#ecc94b' },
  { name: 'cream', hex: '#f5e6c8' },
  // Yellow-Green (90°)
  { name: 'olive', hex: '#6b8e23' },
  { name: 'lime', hex: '#84cc16' },
  // Green (120°)
  { name: 'moss', hex: '#8a9a5b' },
  { name: 'green', hex: '#38a169' },
  { name: 'emerald', hex: '#059669' },
  { name: 'forest', hex: '#2d5a27' },
  // Cyan (180°)
  { name: 'teal', hex: '#319795' },
  { name: 'cyan', hex: '#06b6d4' },
  // Blue (210-240°)
  { name: 'sky', hex: '#38bdf8' },
  { name: 'blue', hex: '#3182ce' },
  { name: 'navy', hex: '#1e3a5f' },
  // Indigo-Purple (260-280°)
  { name: 'indigo', hex: '#5a67d8' },
  { name: 'violet', hex: '#7c3aed' },
  { name: 'purple', hex: '#805ad5' },
  { name: 'lavender', hex: '#b794f4' },
  // Pink-Magenta (300-340°)
  { name: 'magenta', hex: '#d53f8c' },
  { name: 'pink', hex: '#ed64a6' },
  { name: 'rose', hex: '#f43f5e' },
  // Brown (低彩度暖色)
  { name: 'chocolate', hex: '#5c3317' },
  { name: 'brown', hex: '#8b6f47' },
  { name: 'tan', hex: '#d2b48c' },
  { name: 'beige', hex: '#c8b896' },
  // Neutral (無彩色: 暗→明)
  { name: 'black', hex: '#1a202c' },
  { name: 'charcoal', hex: '#4a5568' },
  { name: 'gray', hex: '#a0aec0' },
  { name: 'silver', hex: '#cbd5e0' },
  { name: 'white', hex: '#f7fafc' },
];

export const classifyColor = (hex: string): string => {
  let minDist = Infinity;
  let closest = 'gray';
  for (const group of COLOR_GROUPS) {
    const dist = colorDistance(hex, group.hex);
    if (dist < minDist) {
      minDist = dist;
      closest = group.name;
    }
  }
  return closest;
};
